import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { SurveyLockedScreen } from '@onxite/plan-sdk';
import { queryKeys } from '@/lib/api/queryKeys';
import { publicSurveyApi } from './api';
import { SurveyWizard } from './SurveyWizard';
import { useT } from '@/lib/i18n/useT';

export function PublicSurveyPage() {
  const [params] = useSearchParams();
  const surveyId = params.get('surveyId');
  const stationId = params.get('stationId');
  // `v` is a cache-buster token written into the QR / short URLs by OutputTab
  // (encoded BsEvent.updatedAt). Including it in the React Query key forces a
  // fresh fetch whenever the manager saves config, so consumers don't see a
  // stale game render after a swap.
  const v = params.get('v') ?? '';
  // PRD §11 — Manager preview mode. Set by the in-config "Live preview"
  // dialog. When `true` the SurveyWizard short-circuits its bulkSubmit so
  // stocks/redemptions stay untouched.
  const dryRun = params.get('preview') === '1';
  const t = useT();

  const { data, isLoading, error } = useQuery({
    queryKey: [
      ...queryKeys.surveys.publicObject(surveyId ?? '', stationId ?? ''),
      v,
    ],
    queryFn: () => publicSurveyApi.getObject(Number(surveyId), Number(stationId)),
    enabled: !!surveyId && !!stationId,
  });

  if (!surveyId || !stationId)
    return (
      <div className="grid min-h-screen place-items-center text-center text-white">
        <p>{t('publicSurvey.missingSurveyOrStation')}</p>
      </div>
    );
  if (isLoading)
    return (
      <div className="grid min-h-screen place-items-center text-white">
        {t('common.loading')}
      </div>
    );
  if (error || !data)
    return (
      <div className="grid min-h-screen place-items-center text-white">
        {t('publicSurvey.notFound.title')}
      </div>
    );

  // Caso 15: the backend short-circuits to a `lockedReason`-only payload when
  // the survey is no longer accepting responses (plan cap, expiry, manual
  // disable). The plan-sdk component renders a dignified, branded message
  // instead of a broken wizard — never a raw error to the consumer.
  if (data.lockedReason) {
    return (
      <div className="grid min-h-screen place-items-center bg-[var(--color-xk-bg,#000)] px-6 text-center text-white">
        <SurveyLockedScreen
          reason={data.lockedReason}
          surveyName={data.name}
          className="max-w-md space-y-3"
          messages={{
            LIMIT_EXHAUSTED: t('publicSurvey.locked.limitExhausted'),
            PLAN_EXPIRED: t('publicSurvey.locked.planExpired'),
            MANUAL_DISABLE: t('publicSurvey.locked.manualDisable'),
          }}
        />
      </div>
    );
  }

  return <SurveyWizard data={data} stationId={Number(stationId)} dryRun={dryRun} />;
}
